import React from "react";
import { AutosizeTextarea } from "@/components/chatform/autosizetextarea";
import { CaretCircleDoubleUp } from "@phosphor-icons/react";
import { CurrentChatContext } from "@/components/context";
import { IChat } from "@/lib/types";
import { v7 as uuidv7 } from "uuid";
import { createIMessage } from "@/lib/utils";
import { FileUpload } from "@/components/chatform/fileupload";
import { FileUploadPreview } from "@/components/chatform/fileuploadpreview";

export interface IFileUploadState {
    base64file?: string;
    filename?: string;
    error?: string;
}

export const ChatForm = () => {
    const [textAreaValue, setTextAreaValue] = React.useState<string>("");
    const [fileUploadState, setFileUploadState] = React.useState<IFileUploadState | undefined>(
        undefined
    );
    const { currentChat, updateCurrentChat } = React.useContext(CurrentChatContext);
    const textAreaPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            if (textAreaValue.trim() !== "") {
                sendMessage();
            }
        }
    };

    const sendMessage = () => {
        // TODO If the AI is responding, this is a noop.
        const message = createIMessage(
            "user",
            textAreaValue,
            fileUploadState?.base64file,
            fileUploadState?.filename
        );
        // When a user types in a message, if the current chat is undefined, create a new chat, and
        // set the current chat message to that value.
        if (currentChat === undefined) {
            const newChat: IChat = {
                timestamp: Date.now(),
                id: uuidv7(),
                messages: [message],
            };
            updateCurrentChat(newChat);
        } else {
            // When a user types in a message, if the current chat is defined, add that message to
            // the current chat.
            const newChat: IChat = {
                ...currentChat,
                timestamp: Date.now(),
                messages: [...currentChat.messages, message],
            };
            updateCurrentChat(newChat);
        }
        setFileUploadState(undefined);
        setTextAreaValue("");
    };
    return (
        <>
            <div className={"max-w-3xl mx-auto pb-2 lg:pb-8 pt-2 lg:pt-4 lg:px-8 px-2"}>
                <FileUploadPreview
                    fileUploadState={fileUploadState}
                    setFileUploadState={setFileUploadState}
                />
                <div
                    className={
                        "p-2 w-full flex flex-row border-2 rounded-2xl items-start has-[:focus]:border-primary transition-colors"
                    }
                >
                    <FileUpload
                        fileUploadState={fileUploadState}
                        setFileUploadState={setFileUploadState}
                    />
                    <AutosizeTextarea
                        className={"resize-none border-0 py-0 px-2 pb-0 pt-1 text-md"}
                        name="message"
                        rows={1}
                        autoFocus={true}
                        placeholder={
                            window.innerWidth >= 1024
                                ? "Send a message to Fender Intelligence."
                                : "Send a message..."
                        }
                        onKeyDown={textAreaPress}
                        onChange={(e) => setTextAreaValue(e.currentTarget.value)}
                        value={textAreaValue}
                    ></AutosizeTextarea>
                    <button
                        className={"group disabled:opacity-25 transition-opacity"}
                        type="button"
                        disabled={textAreaValue === ""}
                        onClick={sendMessage}
                    >
                        <CaretCircleDoubleUp
                            size={32}
                            className={
                                "text-zinc-900 hover:text-zinc-900 group-enabled:hover:bg-zinc-200 rounded-full transition-colors"
                            }
                        />
                    </button>
                </div>
            </div>
        </>
    );
};
